import React, {useEffect, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {useAuth} from "../../../../../context/AuthContext";
import {SaveIcon} from "lucide-react";
import SectionHeading from "../../../../../components/SectionHeading";
import type {PaymentFacilitator} from "../../../../../types/checkout";
import TierListEditor, {type TierForm, tiersFromApi, tiersToApi} from "./TierListEditor";

type OutletCtx = { eventId: string | null };

type OptionsForm = {
    featured: boolean;
    cashlessEnabled: boolean;
    staffNeededForScanning: boolean;
    staffNeededForDoorSales: boolean;
    reserveCapacityForPendingOrders: boolean;
    acceptedPaymentMethods: PaymentFacilitator[];
    groupDiscountTiers: TierForm[];
    activationFeeEnabled: boolean;
    activationFeeRefundable: boolean;
    activationFeeAmount: string; // string while editing (mirrors TierForm), number on the wire
    externalSalesEnabled: boolean;
};

const PAYMENT_METHOD_OPTIONS: { value: PaymentFacilitator; label: string; description: string }[] = [
    {value: "FINATIC", label: "Card", description: "Online card payments."},
    {value: "EFT", label: "EFT", description: "Bank transfer / EFT."},
    {value: "INVOICE", label: "Invoice", description: "Invoice billing."},
    {value: "CASH", label: "Cash (pay at event)", description: "Pay in cash at the event."},
];

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

type ToggleRowProps = {
    label: string;
    description: string;
    checked: boolean;
    onChange: (v: boolean) => void;
};

function Card({title, description, children}: {
    title?: string;
    description?: string;
    children: React.ReactNode;
}) {
    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            {(title || description) && (
                <div className="mb-1">
                    {title && <h3 className="text-base font-semibold text-gray-900">{title}</h3>}
                    {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
                </div>
            )}
            <div className="divide-y divide-gray-100">{children}</div>
        </section>
    );
}

function ToggleRow({label, description, checked, onChange}: ToggleRowProps) {
    return (
        <div className="flex flex-col gap-4 py-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">{label}</div>
                <p className="mt-1 text-sm text-gray-600">{description}</p>
            </div>
            <label className="inline-flex items-center gap-3">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    className="size-5 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                />
                <span className={cx("text-sm font-semibold", checked ? "text-gray-900" : "text-gray-500")}>
                    {checked ? "Enabled" : "Disabled"}
                </span>
            </label>
        </div>
    );
}

export function OptionsStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const {user} = useAuth();
    const isAdmin = user?.roles?.includes("ADMINISTRATOR") ?? false;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState<OptionsForm>({
        featured: false,
        cashlessEnabled: false,
        staffNeededForScanning: false,
        staffNeededForDoorSales: false,
        // Matches the backend default, which is now true for every event. Seeding this false
        // would show the toggle off on a slow load and, if the organiser saved before the GET
        // landed, silently turn a hold off that they never touched.
        reserveCapacityForPendingOrders: true,
        acceptedPaymentMethods: [],
        groupDiscountTiers: [],
        activationFeeEnabled: false,
        activationFeeRefundable: false,
        activationFeeAmount: "",
        externalSalesEnabled: false,
    });

    useEffect(() => {
        if (!businessId || !eventId) return;

        setLoading(true);
        (async () => {
            try {
                const {data} = await api.get(`/api/v2/business/${businessId}/events/${eventId}/options`);
                setForm({
                    featured: Boolean(data?.featured ?? false),
                    cashlessEnabled: Boolean(data?.cashlessEnabled ?? false),
                    staffNeededForScanning: Boolean(data?.staffNeededForScanning ?? false),
                    staffNeededForDoorSales: Boolean(data?.staffNeededForDoorSales ?? false),
                    reserveCapacityForPendingOrders: Boolean(data?.reserveCapacityForPendingOrders ?? true),
                    acceptedPaymentMethods: Array.isArray(data?.acceptedPaymentMethods)
                        ? (data.acceptedPaymentMethods as PaymentFacilitator[])
                        : [],
                    groupDiscountTiers: tiersFromApi(data?.groupDiscountTiers),
                    activationFeeEnabled: Boolean(data?.activationFeeEnabled ?? false),
                    activationFeeRefundable: Boolean(data?.activationFeeRefundable ?? false),
                    activationFeeAmount: data?.activationFeeAmount != null ? String(data.activationFeeAmount) : "",
                    externalSalesEnabled: Boolean(data?.externalSalesEnabled ?? false),
                });
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load options"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, eventId]);

    const save = async () => {
        if (!businessId || !eventId) return;

        setSaving(true);
        try {
            const payload: Record<string, unknown> = {
                cashlessEnabled: form.cashlessEnabled,
                staffNeededForScanning: form.staffNeededForScanning,
                staffNeededForDoorSales: form.staffNeededForDoorSales,
                reserveCapacityForPendingOrders: form.reserveCapacityForPendingOrders,
                acceptedPaymentMethods: form.acceptedPaymentMethods,
            };
            if (isAdmin) {
                payload.featured = form.featured;
                payload.activationFeeEnabled = form.activationFeeEnabled;
                payload.activationFeeRefundable = form.activationFeeRefundable;
                const amt = Number(form.activationFeeAmount);
                payload.activationFeeAmount =
                    form.activationFeeAmount.trim() !== "" && Number.isFinite(amt) ? amt : null;
                // Only administrators may change this one, and the API refuses a change from anyone
                // else, so it is left out of the payload entirely for a business admin.
                payload.externalSalesEnabled = form.externalSalesEnabled;
            }
            payload.groupDiscountTiers = tiersToApi(form.groupDiscountTiers);

            await api.patch(`/api/v2/business/${businessId}/events/${eventId}/options`, payload);

            notify.success("Options saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save options"));
        } finally {
            setSaving(false);
        }
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/review`);
        else navigate(`/business/events/new/review?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">

            <SectionHeading
                title={mode === "edit" ? "Edit Options" : "Set Options"}
                description="Operational settings for this event — cashless payments, staffing flags, and platform visibility."
                actions={
                    <>
                        <Button type="button" variant="primary" size="md"
                                onClick={save}
                                disabled={saving}>
                            {saving ? "Saving…" :
                                <span className="inline-flex items-center gap-2"><SaveIcon
                                    className="size-4"/>Save</span>}
                        </Button>
                    </>
                }
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading options…</div>
            ) : (
                <div className="space-y-6">
                    <Card
                        title="Cashless & Payments"
                        description="On-site cashless payments and how buyers can pay for this event."
                    >
                        <div className="py-5">
                            <div className="text-sm font-semibold text-gray-900">Accepted Payment Methods</div>
                            <p className="mt-1 text-sm text-gray-600">
                                Choose which methods buyers can use to pay for this event.
                            </p>
                            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                {PAYMENT_METHOD_OPTIONS.map((opt) => {
                                    const checked = form.acceptedPaymentMethods.includes(opt.value);
                                    return (
                                        <label
                                            key={opt.value}
                                            className={cx(
                                                "flex cursor-pointer items-start gap-3 rounded-xl border p-3",
                                                checked ? "border-orange-600 bg-orange-50" : "border-gray-200 hover:bg-gray-50"
                                            )}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) =>
                                                    setForm((p) => ({
                                                        ...p,
                                                        acceptedPaymentMethods: e.target.checked
                                                            ? [...p.acceptedPaymentMethods, opt.value]
                                                            : p.acceptedPaymentMethods.filter((m) => m !== opt.value),
                                                    }))
                                                }
                                                className="mt-0.5 size-5 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                            />
                                            <span className="min-w-0">
                                                <span className="block text-sm font-semibold text-gray-900">{opt.label}</span>
                                                <span className="mt-0.5 block text-sm text-gray-600">{opt.description}</span>
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <ToggleRow
                            label="Cashless Enabled"
                            description="Enable on-site cashless payments for this event."
                            checked={form.cashlessEnabled}
                            onChange={(v) => setForm((p) => ({...p, cashlessEnabled: v}))}
                        />
                        {isAdmin && form.cashlessEnabled && (
                            <>
                                <ToggleRow
                                    label="Activation Fee"
                                    description="Charge an activation fee for cashless cards at this event. Administrator only."
                                    checked={form.activationFeeEnabled}
                                    onChange={(v) => setForm((p) => ({...p, activationFeeEnabled: v}))}
                                />
                                {form.activationFeeEnabled && (
                                    <div className="py-5">
                                        <label className="block text-sm font-semibold text-gray-900">
                                            Activation Fee Amount (NAD)
                                        </label>
                                        <p className="mt-1 text-sm text-gray-600">
                                            The amount charged per cashless card activation.
                                        </p>
                                        <input
                                            inputMode="decimal"
                                            value={form.activationFeeAmount}
                                            onChange={(e) => setForm((p) => ({...p, activationFeeAmount: e.target.value}))}
                                            placeholder="e.g. 500"
                                            className="mt-3 w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none sm:max-w-[16rem]"
                                        />
                                    </div>
                                )}
                                {form.activationFeeEnabled && (
                                    <ToggleRow
                                        label="Activation Fee Refundable"
                                        description="Whether the cashless activation fee is refundable. Administrator only."
                                        checked={form.activationFeeRefundable}
                                        onChange={(v) => setForm((p) => ({...p, activationFeeRefundable: v}))}
                                    />
                                )}
                            </>
                        )}
                    </Card>

                    <Card
                        title="Staffing"
                        description="Flag whether this event needs staff on the day."
                    >
                        <ToggleRow
                            label="Staff Needed for Scanning"
                            description="Flag that this event requires staff at entry to scan tickets."
                            checked={form.staffNeededForScanning}
                            onChange={(v) => setForm((p) => ({...p, staffNeededForScanning: v}))}
                        />
                        <ToggleRow
                            label="Staff Needed for Door Sales"
                            description="Flag that this event requires staff at the door to sell tickets on the day."
                            checked={form.staffNeededForDoorSales}
                            onChange={(v) => setForm((p) => ({...p, staffNeededForDoorSales: v}))}
                        />
                    </Card>

                    <Card title="Capacity">
                        <ToggleRow
                            label="Hold Capacity for Pending Offline Orders"
                            description="On by default. Unpaid EFT, cash and invoice orders reserve their ticket and seat capacity, so a ticket type can show as sold out and a seat as taken on the strength of orders nobody has paid for yet. That is usually what you want: an invoice you issued is a commitment. Turn it off if you would rather sell the same capacity twice and let payment decide, in which case only paid orders count. Nothing releases an abandoned unpaid order automatically, so cancel the ones you know are dead."
                            checked={form.reserveCapacityForPendingOrders}
                            onChange={(v) => setForm((p) => ({...p, reserveCapacityForPendingOrders: v}))}
                        />
                    </Card>

                    <Card
                        title="Event-wide Group Discount Tiers"
                        description="Automatic quantity discounts applied across the whole order at checkout. The highest qualifying tier applies."
                    >
                        <div className="py-5">
                            <TierListEditor
                                tiers={form.groupDiscountTiers}
                                onChange={(next) => setForm((p) => ({...p, groupDiscountTiers: next}))}
                            />
                        </div>
                    </Card>

                    {isAdmin && (
                        <Card
                            title="Administrator"
                            description="Platform-level settings. Administrator only."
                        >
                            <ToggleRow
                                label="Featured"
                                description="Promote this event to the featured slot on the homepage and category pages."
                                checked={form.featured}
                                onChange={(v) => setForm((p) => ({...p, featured: v}))}
                            />
                            <ToggleRow
                                label="Partner Sales"
                                description="List this event in the catalogue our integration partners sell from, so a third-party app can sell its tickets. Off by default. Switching it off removes the event from those catalogues and refuses new partner orders for it, leaving tickets already sold untouched."
                                checked={form.externalSalesEnabled}
                                onChange={(v) => setForm((p) => ({...p, externalSalesEnabled: v}))}
                            />
                        </Card>
                    )}
                </div>
            )}

            {/* Cross-sell upsells will live here in a future iteration. */}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md"
                        onClick={() => navigate("/business/events")}>
                    Exit
                </Button>
                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={saving}>
                    Continue to Review &amp; Publish
                </Button>
            </div>
        </div>
    );
}
